// /var/www/oszczedzam/client/src/components/transactions/TransactionForm.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Button, TextField, FormControl, InputLabel, 
  Select, MenuItem, FormControlLabel, Checkbox, 
  Chip, Grid, Paper, Typography, IconButton, Autocomplete
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pl } from 'date-fns/locale';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';

// Kategorie wydatków
const expenseCategories = [
  'Mieszkanie', 'Transport', 'Żywność', 'Rozrywka', 'Zdrowie', 
  'Odzież', 'Edukacja', 'Prezenty', 'Podróże', 'Dzieci', 'Sport', 
  'Zwierzęta', 'Elektronika', 'Rachunki', 'Subskrypcje', 'Inne'
];

// Kategorie przychodów
const incomeCategories = [
  'Wynagrodzenie', 'Inwestycje', 'Prezenty', 'Premia', 'Sprzedaż', 'Inne'
];

function TransactionForm({ onSubmit, initialData, isEditing = false }) {
  const [transactionType, setTransactionType] = useState(initialData?.amount >= 0 ? 'income' : 'expense');
  const [title, setTitle] = useState(initialData?.title || '');
  const [amount, setAmount] = useState(initialData ? Math.abs(initialData.amount) : '');
  const [category, setCategory] = useState(initialData?.category || '');
  const [date, setDate] = useState(initialData?.date ? new Date(initialData.date) : new Date());
  const [description, setDescription] = useState(initialData?.description || '');
  const [isRecurring, setIsRecurring] = useState(initialData?.isRecurring || false);
  const [tags, setTags] = useState(initialData?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});

  const categories = transactionType === 'income' ? incomeCategories : expenseCategories;

  // Walidacja formularza
  const validateForm = () => {
    const newErrors = {};
    
    if (!title.trim()) newErrors.title = 'Tytuł jest wymagany';
    if (!amount || isNaN(amount) || amount <= 0) newErrors.amount = 'Kwota musi być większa od zera';
    if (!category) newErrors.category = 'Kategoria jest wymagana';
    if (!date) newErrors.date = 'Data jest wymagana';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Obsługa dodawania tagu
  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  // Obsługa usuwania tagu
  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter(tag => tag !== tagToDelete));
  };

  // Obsługa zatwierdzania formularza
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Przygotuj dane transakcji
    const transactionData = {
      title,
      amount: transactionType === 'expense' ? -Math.abs(parseFloat(amount)) : Math.abs(parseFloat(amount)),
      category,
      date: date.toISOString().split('T')[0],
      description,
      isRecurring,
      tags
    };
    
    // Wywołaj funkcję przekazaną przez props
    onSubmit(transactionData);
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {isEditing ? 'Edytuj transakcję' : 'Nowa transakcja'}
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Typ transakcji */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="transaction-type-label">Typ transakcji</InputLabel>
              <Select
                labelId="transaction-type-label"
                id="transaction-type"
                value={transactionType}
                label="Typ transakcji"
                onChange={(e) => setTransactionType(e.target.value)}
              >
                <MenuItem value="expense">Wydatek</MenuItem>
                <MenuItem value="income">Przychód</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Tytuł */}
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="title"
              label="Tytuł"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
            />
          </Grid>
          
          {/* Kwota */}
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="amount"
              label="Kwota"
              type="number"
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={!!errors.amount}
              helperText={errors.amount}
            />
          </Grid>
          
          {/* Kategoria */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.category}>
              <InputLabel id="category-label">Kategoria</InputLabel>
              <Select
                labelId="category-label"
                id="category"
                value={category}
                label="Kategoria"
                onChange={(e) => setCategory(e.target.value)}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
              {errors.category && <Typography color="error" variant="caption">{errors.category}</Typography>}
            </FormControl>
          </Grid>
          
          {/* Data */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
              <DatePicker
                label="Data"
                value={date}
                onChange={(newDate) => setDate(newDate)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.date,
                    helperText: errors.date
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          {/* Opis */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="description"
              label="Opis (opcjonalnie)"
              multiline
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          
          {/* Cykliczność */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={isRecurring} 
                  onChange={(e) => setIsRecurring(e.target.checked)}
                />
              }
              label="Transakcja cykliczna"
            />
          </Grid>
          
          {/* Tagi */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Tagi (opcjonalnie)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TextField
                size="small"
                id="tag-input"
                label="Dodaj tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                sx={{ mr: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddTag}
                startIcon={<AddIcon />}
              >
                Dodaj
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                  size="small"
                />
              ))}
            </Box>
          </Grid>
          
          {/* Przyciski */}
          <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" type="submit">
              {isEditing ? 'Zapisz zmiany' : 'Dodaj transakcję'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default TransactionForm;